<template>
  <div class="cancel-modal">
    <p class="cancel-modal__description">Välj en eller flera anledningar till makulering:</p>

    <div class="cancel-modal__reasons">
      <AppCheckbox
        v-for="reason in reasons"
        :key="reason.code"
        :model-value="selectedCodes.has(reason.code)"
        hide-details
        @update:model-value="toggleCode(reason.code, $event)"
      >
        {{ reason.label }}
      </AppCheckbox>
    </div>

    <div class="cancel-modal__actions">
      <AppButton :disabled="isSubmitting" @click="emit('close')">Avbryt</AppButton>
      <AppButton color="blue" :loading="isSubmitting" @click="submit"> Makulera </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { AppButton, AppCheckbox, useNotifications } from '@staff/ui'

import { cancelApplication, ApiRequestError } from '@/services/application'
import type { CancelReason } from '@/services/application/application.types'
import { useMetadata } from '@/composables/useMetadata'
import { CANCEL_REASON_CODES } from './cancel-application.constants'

const props = defineProps<{
  applicationId: string
  onSuccess: () => void | Promise<void>
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { getReferenceValues } = useMetadata()

const reasons = computed(() =>
  CANCEL_REASON_CODES.map((code) => ({
    code,
    label: getReferenceValues('cancelReasons').find((r) => r.code === code)?.label ?? code,
  })),
)

const { notify } = useNotifications()

const selectedCodes = ref<Set<CancelReason>>(new Set())
const isSubmitting = ref(false)

const toggleCode = (code: CancelReason, checked: boolean) => {
  const next = new Set(selectedCodes.value)
  if (checked) {
    next.add(code)
  } else {
    next.delete(code)
  }
  selectedCodes.value = next
}

const submit = async () => {
  if (isSubmitting.value) return

  isSubmitting.value = true

  try {
    const cancelReasons = [...selectedCodes.value].map((code) => ({ code }))
    await cancelApplication(props.applicationId, cancelReasons)
    await props.onSuccess()
    emit('close')
  } catch (err) {
    const title = err instanceof Error ? err.message : 'Ett fel uppstod vid makulering.'
    const description = err instanceof ApiRequestError && err.details.length ? err.details.join(', ') : undefined
    notify({
      type: 'error',
      title,
      description,
      dismissable: true,
    })
    emit('close')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped lang="scss">
.cancel-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 0 16px;
}

.cancel-modal__description {
  margin: 0;
  color: var(--color-fg-default);
}

.cancel-modal__reasons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 400px;
  overflow-y: auto;
}

.cancel-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
